import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  UploadAbortedError,
  type PlayerRegistrationDocument,
  type PublicOrderDocumentsPayload,
} from "../lib/api";
import { shrinkImageToFit } from "../lib/imageShrink";
import { makeLatestOnly } from "../lib/latestOnly";
import { showGlobalToast } from "./GlobalToasts";

/** Ancla de la tarjeta: el home del jugador y el recordatorio del organizador
 * mandan aquí con `#documentos` (backend: PLAYER_DOCUMENTS_ANCHOR en
 * registrations/links.py). */
export const PLAYER_DOCUMENTS_ANCHOR = "documentos";

/** Código del servidor cuando el documento ya estaba recibido. */
const ALREADY_RECEIVED_CODE = "registration_document_already_received";

/** Lo que la tarjeta necesita de la respuesta: igual en la vista del jugador
 * (sesión) y en el link público de la orden (sin sesión). */
type DocumentsPayload = PublicOrderDocumentsPayload;

/** Link público de la orden (Eduardo 2026-10-29): quien aún no tiene acceso
 * sube sus pendientes sin cuenta; lo autoriza el token de la orden. */
export interface PublicOrderDocumentsSource {
  slug: string;
  orderToken: string;
}

interface PlayerPendingDocumentsCardProps {
  tournamentId: number;
  /** Perfil que se está viendo en el torneo (cuenta familiar: el hijo
   * elegido). `null` mientras no se sabe o se está cambiando de hijo: no se
   * pide ni se muestra nada. */
  profileId: number | null;
  /** Avisa mientras sube un archivo, para no cambiar de hijo a media subida. */
  onBusyChange?: (busy: boolean) => void;
  /** Con esto la tarjeta trabaja con el link público de la orden: no usa la
   * sesión ni `tournamentId`/`profileId`. */
  publicOrder?: PublicOrderDocumentsSource | null;
}

function pickLabel(doc: PlayerRegistrationDocument): string {
  return doc.kind === "image" ? "Tomar o elegir foto" : "Elegir archivo PDF";
}

/** Nombre accesible del selector: EMPIEZA con el texto visible del botón (quien
 * usa control por voz dice lo que ve) y añade de qué documento se trata. */
function pickAccessibleName(doc: PlayerRegistrationDocument): string {
  return `${pickLabel(doc)}: ${doc.label}`;
}

function acceptsType(doc: PlayerRegistrationDocument, type: string): boolean {
  // Sin tipo declarado (pasa en algunos celulares) decide el servidor.
  if (!type) return true;
  return doc.accept
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .includes(type.toLowerCase());
}

/** ¿Vale la pena ofrecer «Reintentar»? Un perfil que no es de la cuenta, un
 * torneo que no existe o una sesión caducada no se arreglan reintentando. */
function isRetryableLoadError(err: unknown): boolean {
  return !(err instanceof ApiError && [401, 403, 404].includes(err.status));
}

/** Aviso de la tarjeta: `ok` en verde; `warning` en ámbar (la CURP parece de
 * otra persona). */
interface CardNotice {
  text: string;
  tone: "ok" | "warning";
}

/** Lo que se le dice a la familia al recibir un documento.
 *
 * Revisión de CURP vs nombre (Eduardo 2026-10-01): en la CURP el servidor
 * manda SOLO el resultado de compararla con la inscripción (nunca el nombre ni
 * la fecha que dice el archivo). Si parece de otra persona se avisa en ámbar y
 * se dice cómo arreglarlo; si no se pudo leer, una línea suave. «Parecido» no
 * cambia nada. La revisión AVISA, nunca bloquea: el documento ya quedó
 * recibido. */
function receivedNotice(
  doc: PlayerRegistrationDocument,
  uploaded: DocumentsPayload["uploaded"],
  playerName: string | null | undefined,
): { notice: CardNotice; toast: "success" | "info" } {
  const check = doc.key === "curp_pdf" ? uploaded?.curp_check : null;
  if (check === "other_person") {
    const name = playerName?.trim();
    const text =
      `Recibimos la CURP, pero el nombre o la fecha de nacimiento no coinciden con los de ${name || "la inscripción"}. ` +
      "Si te equivocaste de archivo, avísale al organizador: él te la puede volver a pedir.";
    return { notice: { text, tone: "warning" }, toast: "info" };
  }
  if (check === "unreadable") {
    const text = `Recibimos tu documento: ${doc.label}. No pudimos leer el texto del PDF; el organizador la revisará.`;
    return { notice: { text, tone: "ok" }, toast: "success" };
  }
  const text = `Recibimos tu documento: ${doc.label}. El organizador ya lo puede revisar.`;
  return { notice: { text, tone: "ok" }, toast: "success" };
}

/**
 * «Documentos pendientes» dentro del torneo (Eduardo 2026-09-30): lo que el
 * organizador todavía espera de la inscripción del jugador (foto, CURP) y un
 * botón para subirlo desde el celular. Usa la misma definición que ve el
 * organizador en Inscripciones; al subir, el documento queda «Recibido» (no
 * hay revisión por documento; la CURP sí se compara con la inscripción y, si
 * parece de otra persona, se avisa sin bloquear). No aparece si no hay nada
 * pendiente o si el torneo ya cerró. Si la consulta FALLA sí aparece, en
 * chico y con «Reintentar»: el home manda aquí con «Subir documentos» y una
 * página sin nada que hacer dejaba a la familia dando vueltas.
 *
 * Todo el contenido lleva `data-no-toast`: describe el estado de la pantalla.
 * El resultado de cada subida (una acción del jugador) sí sale como aviso.
 */
export default function PlayerPendingDocumentsCard({
  tournamentId,
  profileId,
  onBusyChange,
  publicOrder = null,
}: PlayerPendingDocumentsCardProps) {
  const auth = useAuth();
  const publicSlug = publicOrder?.slug ?? null;
  const publicToken = publicOrder?.orderToken ?? null;
  const isPublic = publicSlug !== null && publicToken !== null;
  // En el link público no cuenta la sesión que haya en el navegador: lo
  // autoriza el token de la orden (ni la suplantación lo bloquea ni el token
  // de sesión viaja).
  const token = isPublic ? null : auth.token;
  const isImpersonating = isPublic ? false : auth.isImpersonating;
  /** De quién son los datos: el perfil (sesión) o la orden (link público).
   * Al cambiar de hijo u orden no se pinta (ni se sube) con lo del anterior. */
  const scopeKey: string | null = isPublic
    ? `orden:${publicSlug}:${publicToken}`
    : token && Number.isFinite(tournamentId) && profileId !== null
      ? `perfil:${tournamentId}:${profileId}`
      : null;
  const latestLoad = useMemo(() => makeLatestOnly(), []);
  const [loaded, setLoaded] = useState<{ scopeKey: string; payload: DocumentsPayload } | null>(null);
  // Alcance cuya consulta falló (para ofrecer «Reintentar» solo a ese).
  const [failedScopeKey, setFailedScopeKey] = useState<string | null>(null);
  // Link público que el servidor ya no reconoce (404: token ajeno o el slug
  // del torneo cambió). Se dice claro en vez de esconder la sección.
  const [invalidLinkScopeKey, setInvalidLinkScopeKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [receivedNow, setReceivedNow] = useState<string[]>([]);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<CardNotice | null>(null);
  // Cada subida terminada pide devolver el foco al título de la tarjeta.
  const [focusRequest, setFocusRequest] = useState(0);
  const scopeRef = useRef<string | null>(scopeKey);
  const busyRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    scopeRef.current = scopeKey;
  }, [scopeKey]);

  const load = useCallback(async () => {
    if (scopeKey === null) {
      latestLoad.invalidate();
      setLoaded(null);
      setFailedScopeKey(null);
      setInvalidLinkScopeKey(null);
      setLoading(false);
      return;
    }
    const attempt = latestLoad.begin();
    setLoading(true);
    try {
      const response =
        publicSlug !== null && publicToken !== null
          ? await api.publicPayments.orderDocuments(publicSlug, publicToken)
          : await api.player.registrationDocuments(token as string, tournamentId, profileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setLoaded({ scopeKey, payload: response.data });
      setFailedScopeKey(null);
      setInvalidLinkScopeKey(null);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      // No bloquea el resto de la página, pero tampoco desaparece en
      // silencio: queda una tarjeta chica con «Reintentar».
      setLoaded(null);
      setFailedScopeKey(isRetryableLoadError(err) ? scopeKey : null);
      setInvalidLinkScopeKey(
        publicSlug !== null && err instanceof ApiError && err.status === 404 ? scopeKey : null,
      );
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, profileId, publicSlug, publicToken, scopeKey, token, tournamentId]);

  useEffect(() => {
    setReceivedNow([]);
    setErrors({});
    setNotice(null);
    void load();
  }, [load]);

  useEffect(
    () => () => {
      latestLoad.invalidate();
      // Al salir de la pantalla no se deja una subida corriendo sin dueño.
      abortRef.current?.abort();
    },
    [latestLoad],
  );

  useEffect(() => {
    onBusyChange?.(busyKey !== null);
  }, [busyKey, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  const current = loaded && scopeKey !== null && loaded.scopeKey === scopeKey ? loaded : null;
  const payload = current?.payload ?? null;

  const handleFile = useCallback(
    async (doc: PlayerRegistrationDocument, picked: File) => {
      if (!current || busyRef.current || isImpersonating) return;
      if (!isPublic && (!token || profileId === null)) return;
      const targetScope = current.scopeKey;
      const targetProfileId = profileId;
      const { max_bytes: maxBytes, max_size_label: maxSizeLabel } = current.payload;
      const stillHere = () => scopeRef.current === targetScope;
      const fail = (message: string) => {
        if (!stillHere()) return;
        setErrors((previous) => ({ ...previous, [doc.key]: message }));
        showGlobalToast(message, "error");
      };
      const controller = new AbortController();
      abortRef.current = controller;
      busyRef.current = true;
      setBusyKey(doc.key);
      setProgress(null);
      // Un intento nuevo limpia los avisos del anterior (de cualquier documento).
      setNotice(null);
      setErrors({});
      try {
        if (picked.size === 0) {
          fail("El archivo está vacío. Elige otro.");
          return;
        }
        if (!acceptsType(doc, picked.type)) {
          // Mismo texto que el servidor («La CURP debe ser un archivo PDF.»).
          fail(doc.type_error || `El archivo debe ser ${doc.formats_label}.`);
          return;
        }
        let file = picked;
        if (file.size > maxBytes) {
          // Foto de cámara demasiado pesada: se achica aquí antes de mandarla.
          const smaller = doc.kind === "image" ? await shrinkImageToFit(file, maxBytes) : null;
          if (!smaller) {
            fail(`El archivo pesa más de ${maxSizeLabel}. Sube uno más ligero.`);
            return;
          }
          file = smaller;
        }
        const onProgress = (fraction: number) => {
          if (stillHere()) setProgress(fraction);
        };
        const response =
          publicSlug !== null && publicToken !== null
            ? await api.publicPayments.uploadOrderDocument(publicSlug, publicToken, doc.key, file, onProgress, {
                signal: controller.signal,
              })
            : await api.player.uploadRegistrationDocument(
                token as string,
                tournamentId,
                targetProfileId as number,
                doc.key,
                file,
                onProgress,
                { signal: controller.signal },
              );
        // La pantalla ya cambió a otro hijo: el archivo quedó guardado para
        // el que lo subió, pero no se pinta en el nuevo.
        if (!stillHere()) return;
        latestLoad.invalidate();
        setLoaded({ scopeKey: targetScope, payload: response.data });
        setReceivedNow((previous) => (previous.includes(doc.key) ? previous : [...previous, doc.key]));
        const { notice: received, toast } = receivedNotice(
          doc,
          response.data.uploaded,
          response.data.player_name ?? current.payload.player_name,
        );
        setNotice(received);
        showGlobalToast(received.text, toast);
        setFocusRequest((previous) => previous + 1);
      } catch (err) {
        if (err instanceof UploadAbortedError) {
          if (err.reason === "stalled") {
            fail("La subida se detuvo. Revisa tu señal e intenta otra vez.");
          } else if (stillHere()) {
            setNotice({ text: `Cancelaste la subida. ${doc.label} sigue pendiente.`, tone: "ok" });
          }
          // Pudo alcanzar a llegar antes del corte: se pregunta al servidor.
          if (stillHere()) void load();
        } else if (err instanceof ApiError && err.code === ALREADY_RECEIVED_CODE) {
          // No es un error de la familia: ya estaba (lo subió el otro papá, el
          // organizador, o la pantalla mostraba una respuesta vieja).
          if (stillHere()) {
            const message = "Este documento ya lo habíamos recibido.";
            setReceivedNow((previous) => (previous.includes(doc.key) ? previous : [...previous, doc.key]));
            setNotice({ text: message, tone: "ok" });
            showGlobalToast(message, "info");
            setFocusRequest((previous) => previous + 1);
            void load();
          }
        } else if (err instanceof ApiError) {
          fail(err.message || "No pudimos subir el documento. Intenta otra vez.");
          // El estado cambió en el servidor (el torneo cerró): se vuelve a
          // consultar para no ofrecer algo inválido.
          if (err.status === 409 && stillHere()) void load();
        } else {
          fail("Error de red. Revisa tu conexión e intenta otra vez.");
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        busyRef.current = false;
        setBusyKey(null);
        setProgress(null);
      }
    },
    [current, isImpersonating, isPublic, latestLoad, load, profileId, publicSlug, publicToken, token, tournamentId],
  );

  const pendingCount = payload?.pending_count ?? 0;
  const visible = Boolean(
    payload && payload.can_upload && payload.documents.length > 0 && (pendingCount > 0 || receivedNow.length > 0),
  );
  const failed = !visible && failedScopeKey !== null && failedScopeKey === scopeKey;
  const invalidLink = !visible && !failed && invalidLinkScopeKey !== null && invalidLinkScopeKey === scopeKey;
  const shown = visible || failed || invalidLink;

  // Llegada con `#documentos` (home del jugador o recordatorio del
  // organizador): la tarjeta se trae a la vista.
  useEffect(() => {
    if (!shown || scrolledRef.current) return;
    scrolledRef.current = true;
    if (typeof window === "undefined" || window.location.hash !== `#${PLAYER_DOCUMENTS_ANCHOR}`) return;
    const node = sectionRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [shown]);

  // Al terminar una subida el selector de archivo desaparece (el renglón pasa
  // a «Recibido») y el foco se iba al inicio de la página: con teclado o
  // lector de pantalla había que volver a buscar la tarjeta para el segundo
  // documento. Se deja en el título de la tarjeta.
  useEffect(() => {
    if (focusRequest > 0) headingRef.current?.focus();
  }, [focusRequest]);

  if (failed) {
    return (
      <article
        ref={sectionRef}
        id={PLAYER_DOCUMENTS_ANCHOR}
        className="player-documents-card player-documents-card--failed"
        aria-labelledby="player-documents-card-title"
        data-no-toast
      >
        <header className="player-documents-card__head">
          <h2 id="player-documents-card-title">Documentos de la inscripción</h2>
        </header>
        <p className="player-documents-card__intro">No pudimos cargar tus documentos pendientes.</p>
        <button
          type="button"
          className="button player-documents-card__retry"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? "Cargando…" : "Reintentar"}
        </button>
      </article>
    );
  }

  if (invalidLink) {
    // Mismo texto para cualquier 404: no se dice si falló el token o el torneo.
    return (
      <article
        ref={sectionRef}
        id={PLAYER_DOCUMENTS_ANCHOR}
        className="player-documents-card player-documents-card--failed"
        aria-labelledby="player-documents-card-title"
        data-no-toast
      >
        <header className="player-documents-card__head">
          <h2 id="player-documents-card-title">Documentos de la inscripción</h2>
        </header>
        <p className="player-documents-card__intro" role="status">
          Este enlace ya no es válido; pide al organizador uno nuevo.
        </p>
      </article>
    );
  }

  if (!visible || !payload) return null;

  const complete = pendingCount === 0;
  const playerName = payload.player_name?.trim();
  const percent = progress === null ? null : Math.round(progress * 100);
  const uploadingDoc = busyKey ? payload.documents.find((doc) => doc.key === busyKey) : null;

  return (
    <article
      ref={sectionRef}
      id={PLAYER_DOCUMENTS_ANCHOR}
      className={`player-documents-card${complete ? " player-documents-card--complete" : ""}`}
      aria-labelledby="player-documents-card-title"
      data-no-toast
    >
      <header className="player-documents-card__head">
        <h2 id="player-documents-card-title" ref={headingRef} tabIndex={-1}>
          {complete ? "Documentos completos" : "Documentos pendientes"}
        </h2>
        {!complete ? (
          <span className="player-documents-card__count">{pendingCount === 1 ? "Falta 1" : `Faltan ${pendingCount}`}</span>
        ) : null}
      </header>
      <p className="player-documents-card__intro">
        {complete
          ? `Ya recibimos todos los documentos${playerName ? ` de ${playerName}` : ""}. No hace falta subir nada más.`
          : `Para completar la inscripción${playerName ? ` de ${playerName}` : ""} falta subir:`}
      </p>
      {isImpersonating && !complete ? (
        <p className="player-documents-card__intro player-documents-card__locked-note">
          En la vista como jugador no se suben documentos: hazlo desde Inscripciones.
        </p>
      ) : null}
      {/* Un lector de pantalla oye que empezó la subida UNA vez (el porcentaje
          cambia decenas de veces y no se anuncia); el resultado llega por el
          aviso de abajo o por el error del renglón. */}
      <p className="sr-only" role="status" aria-live="polite">
        {uploadingDoc ? `Subiendo ${uploadingDoc.label}…` : ""}
      </p>
      <ul className="player-documents-card__list">
        {payload.documents.map((doc) => {
          const pending = doc.status === "pending";
          const uploading = busyKey === doc.key;
          const error = errors[doc.key];
          return (
            <li
              key={doc.key}
              className={`player-documents-card__item${pending ? "" : " player-documents-card__item--received"}`}
            >
              <div className="player-documents-card__copy">
                <strong>{doc.label}</strong>
                {pending ? (
                  <>
                    <small>
                      Formatos: {doc.formats_label} · Máximo {payload.max_size_label}
                    </small>
                    {doc.hint ? (
                      <small className="player-documents-card__hint">
                        {doc.hint}
                        {doc.help_link ? (
                          <>
                            {" "}
                            <a href={doc.help_link.url} target="_blank" rel="noopener noreferrer">
                              {doc.help_link.label}
                            </a>
                          </>
                        ) : null}
                      </small>
                    ) : null}
                  </>
                ) : (
                  <small>El organizador ya lo tiene.</small>
                )}
              </div>
              {!pending ? (
                <span className="player-documents-card__badge">✓ Recibido</span>
              ) : uploading ? (
                <div className="player-documents-card__progress">
                  <progress max={100} value={percent ?? undefined} aria-label={`Avance de ${doc.label}`} />
                  <span>
                    {percent === null ? "Subiendo…" : percent >= 100 ? "Guardando…" : `Subiendo… ${percent} %`}
                  </span>
                  <button
                    type="button"
                    className="button button--ghost player-documents-card__cancel"
                    aria-label={`Cancelar la subida de ${doc.label}`}
                    onClick={() => abortRef.current?.abort()}
                  >
                    Cancelar
                  </button>
                </div>
              ) : isImpersonating ? (
                <span className="player-documents-card__badge player-documents-card__badge--pending">Pendiente</span>
              ) : (
                <label className={`button player-documents-card__pick${busyKey !== null ? " is-disabled" : ""}`}>
                  {pickLabel(doc)}
                  <input
                    type="file"
                    accept={doc.accept}
                    disabled={busyKey !== null}
                    aria-label={pickAccessibleName(doc)}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = "";
                      if (file) void handleFile(doc, file);
                    }}
                  />
                </label>
              )}
              {error ? (
                <p className="form-error player-documents-card__error" role="alert">
                  {error}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
      {notice ? (
        <p
          className={`${notice.tone === "warning" ? "alert alert--warning" : "form-message"} player-documents-card__notice`}
          role="status"
        >
          {notice.text}
        </p>
      ) : null}
    </article>
  );
}
